import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { useGmSession } from '../useGmSession';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createPacManSession,
    deletePacManSession,
    movePacManSession,
    PELLET_POINTS,
    POWER_POINTS,
    GHOST_POINTS,
    CLEAR_BONUS,
} from './pacmanGmClient';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'pac_man';
const { UPLOAD_URL } = ARCADE.pac_man;
const ACCENT = GAME_ACCENT.pac_man;
const TILE_SIZE = 24;
const HINT = 'Arrows / WASD move · Esc pause';
const TICK_MS = 160;
const GRID_WIDTH = 13;
const FALLBACK_LAYOUT = [
    '#############',
    '#...........#',
    '#.###.###.###',
    '#o#.....#...#',
    '#.###.#.#.#.#',
    '#.....#...#o#',
    '###.#.###.#.#',
    '#...#.....#.#',
    '#.#####.###.#',
    '#...........#',
    '#############',
];

export { PELLET_POINTS, POWER_POINTS, GHOST_POINTS, CLEAR_BONUS };

const emptyView = () => ({
    grid: FALLBACK_LAYOUT,
    pac: { position: [5, 6], direction: 'left' },
    ghosts: [],
    pellets: [],
    power_pellets: [],
    score: 0,
    lives: 3,
    tick: 0,
    isGameOver: false,
    isWin: false,
    powerMode: 0,
    message: '',
});

const PacMan = () => {
    const play = useManualPlay();
    const [view, setView] = useState(emptyView);
    const viewRef = useRef(view);
    const phaseRef = useRef(play.phase);
    const dirQueueRef = useRef(null);
    const inFlightRef = useRef(false);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });
    const gm = useGmSession({
        createSession: createPacManSession,
        deleteSession: deletePacManSession,
    });

    useEffect(() => { viewRef.current = view; }, [view]);
    useEffect(() => { phaseRef.current = play.phase; }, [play.phase]);

    const applyGm = useCallback((data) => {
        const next = {
            grid: Array.isArray(data.grid) ? data.grid : FALLBACK_LAYOUT,
            pac: {
                position: data.pac?.position || [5, 6],
                direction: data.pac?.direction || 'left',
            },
            ghosts: Array.isArray(data.ghosts) ? data.ghosts : [],
            pellets: Array.isArray(data.pellets) ? data.pellets : [],
            power_pellets: Array.isArray(data.power_pellets) ? data.power_pellets : [],
            score: typeof data.score === 'number' ? data.score : 0,
            lives: typeof data.lives === 'number' ? data.lives : 3,
            tick: typeof data.tick === 'number' ? data.tick : 0,
            isGameOver: Boolean(data.is_game_over),
            isWin: Boolean(data.is_win),
            powerMode: typeof data.power_mode === 'number' ? data.power_mode : 0,
            message: data.message || '',
        };
        viewRef.current = next;
        setView(next);
        return next;
    }, []);

    const startGame = useCallback(async () => {
        try {
            gm.setBusy(true);
            const data = await gm.createFreshSession();
            const next = applyGm(data);
            dirQueueRef.current = null;
            recorder.reset({
                init_state: {
                    pellets: next.pellets,
                    power_pellets: next.power_pellets,
                    ghosts: next.ghosts,
                },
                grid: next.grid,
            });
            play.startGame();
        } catch (err) {
            console.error(err);
            setView((v) => ({ ...v, message: gmStartErrorMessage(err) }));
        } finally {
            gm.setBusy(false);
        }
    }, [applyGm, gm, play, recorder]);

    const endGame = useCallback(async () => {
        const finalScore = viewRef.current.score;
        await gm.clearSession();
        play.endGame({ score: finalScore });
        const next = emptyView();
        viewRef.current = next;
        setView(next);
        dirQueueRef.current = null;
    }, [gm, play]);

    const tick = useCallback(async () => {
        if (phaseRef.current !== 'playing') return;
        if (viewRef.current.isGameOver) return;
        if (inFlightRef.current) return;
        const sid = gm.sessionIdRef.current;
        if (!sid) return;
        inFlightRef.current = true;
        try {
            const dir = dirQueueRef.current;
            const data = await movePacManSession(sid, dir);
            const next = applyGm(data);
            recorder.recordTick({
                position: next.pac.position,
                direction: next.pac.direction,
                score: next.score,
                lives: next.lives,
                power_mode: next.powerMode,
                ghosts: next.ghosts.map((g) => ({
                    position: g.position,
                    mode: g.mode,
                })),
            });
            if (next.isGameOver) {
                play.markOver();
                recorder.finalizeAndUpload({
                    score: next.score,
                    extra: { lives: next.lives, is_win: next.isWin },
                });
                await gm.clearSession();
            }
        } catch (err) {
            console.warn('Pac-Man GM tick failed', err);
        } finally {
            inFlightRef.current = false;
        }
    }, [applyGm, gm, play, recorder]);

    useEffect(() => {
        if (play.phase !== 'playing') return undefined;
        const id = setInterval(() => { tick(); }, TICK_MS);
        return () => clearInterval(id);
    }, [play.phase, tick]);

    const onKey = useCallback((e) => {
        if (['ArrowUp', 'w', 'W'].includes(e.key)) { dirQueueRef.current = 'up'; e.preventDefault(); }
        else if (['ArrowDown', 's', 'S'].includes(e.key)) { dirQueueRef.current = 'down'; e.preventDefault(); }
        else if (['ArrowLeft', 'a', 'A'].includes(e.key)) { dirQueueRef.current = 'left'; e.preventDefault(); }
        else if (['ArrowRight', 'd', 'D'].includes(e.key)) { dirQueueRef.current = 'right'; e.preventDefault(); }
    }, []);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [],
    });

    const layout = view.grid;
    const cellContent = (rowIdx, colIdx) => {
        const [pr, pc] = view.pac.position || [];
        if (pr === rowIdx && pc === colIdx) {
            return <Box sx={{ width: TILE_SIZE * 0.65, height: TILE_SIZE * 0.65, borderRadius: '50%', background: ACCENT }} />;
        }
        const ghostHere = view.ghosts.find((g) => g.position?.[0] === rowIdx && g.position?.[1] === colIdx);
        if (ghostHere) {
            const color = ghostHere.mode === 'fright' ? '#64B5F6' : '#EC407A';
            return <Box sx={{
                width: TILE_SIZE * 0.65,
                height: TILE_SIZE * 0.65,
                borderRadius: `${TILE_SIZE * 0.35}px ${TILE_SIZE * 0.35}px ${TILE_SIZE * 0.12}px ${TILE_SIZE * 0.12}px`,
                background: color,
            }}
            />;
        }
        if (view.pellets.some(([r, c]) => r === rowIdx && c === colIdx)) {
            return <Box sx={{ width: 5, height: 5, background: '#FFFDE7', borderRadius: '50%' }} />;
        }
        if (view.power_pellets.some(([r, c]) => r === rowIdx && c === colIdx)) {
            return <Box sx={{ width: 10, height: 10, background: '#FF7043', borderRadius: '50%' }} />;
        }
        return null;
    };

    const pelletsRemaining = view.pellets.length + view.power_pellets.length;

    return (
        <ArcadeGameShell
            title="Pac-Man"
            titleAccent="Maze"
            subtitle="Chomp pellets, dodge ghosts — manual pad or agent"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.pac_man}
            session={session}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Score ${view.score}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`Lives ${view.lives}`} size="small" color={view.lives <= 1 ? 'error' : 'success'} />
                        <Chip label={`Pellets ${pelletsRemaining}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                    </Stack>
                    {view.message && play.phase !== 'over' && <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{view.message}</Typography>}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${GRID_WIDTH}, ${TILE_SIZE}px)`,
                        gap: '1px',
                        p: 1,
                        mx: 'auto',
                        bgcolor: '#000',
                        borderRadius: 2,
                        border: `1px solid ${COLOR.border}`,
                        maxWidth: '100%',
                        overflow: 'auto',
                        width: 'fit-content',
                    }}
                    >
                        {layout.map((row, rIdx) => [...row].map((cell, cIdx) => (
                            <Box
                                key={`${rIdx}-${cIdx}`}
                                sx={{
                                    width: TILE_SIZE,
                                    height: TILE_SIZE,
                                    bgcolor: cell === '#' ? '#1a237e' : COLOR.bg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {cellContent(rIdx, cIdx)}
                            </Box>
                        )))}
                    </Box>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default PacMan;
